import React from 'react';
import { Container, Grid } from '@mui/material';
import LinkButtonCard from '../../components/shared/link-button-card';
import { useGlobalState } from '../../global/context/global-context';
import { HomeSectionTitle, PageSection } from '../home.styles';
import styled from 'styled-components';

// Material-UI Icons
import NewspaperIcon from '@mui/icons-material/Newspaper';
import GavelIcon from '@mui/icons-material/Gavel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoIcon from '@mui/icons-material/Info';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import HelpIcon from '@mui/icons-material/Help';
import ReceiptIcon from '@mui/icons-material/Receipt';
import FlightIcon from '@mui/icons-material/Flight';
import BadgeIcon from '@mui/icons-material/Badge';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import BalanceIcon from '@mui/icons-material/Balance';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import HandshakeIcon from '@mui/icons-material/Handshake';
import VerifiedIcon from '@mui/icons-material/Verified';

interface ImportantLink {
  title: string;
  href: string;
  icon: React.ReactNode;
  external?: boolean;
}

const CategoryTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #0a5079;
  margin: 0 0 24px 0;
  padding-bottom: 12px;
  border-bottom: 3px solid #2786bd;
  display: inline-block;
`;

const CategorySection = styled.div`
  margin-bottom: 48px;
`;

export default function SectionImportantLinks() {
  const { applicationData, parametersPortal } = useGlobalState();
  const cnpj = applicationData?.entity?.document || '';

  // Gera o nome da cidade sem espaços para o SEI
  const cityNameNoSpaces = applicationData?.entity?.address?.city
    ?.toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '')
    || '';

  // Links para Cidadãos
  const citizenLinks: ImportantLink[] = [
    {
      title: 'Consultar Diário Oficial',
      href: '/diario-oficial',
      icon: <NewspaperIcon />,
      external: false,
    },
    {
      title: 'Acompanhar Licitações',
      href: '/licitacoes',
      icon: <GavelIcon />,
      external: false,
    },
    {
      title: 'Transparência Pública',
      href: '/contas-publicas',
      icon: <VisibilityIcon />,
      external: false,
    },
    {
      title: 'Sistema e-SIC',
      href: `http://airdoc.com.br/sic?codEntidade=${cnpj}&empresa=3851`,
      icon: <InfoIcon />,
      external: true,
    },
    {
      title: 'Sistema e-OUV',
      href: `http://airdoc.com.br/ouvidoria?codEntidade=${cnpj}&empresa=3851`,
      icon: <RecordVoiceOverIcon />,
      external: true,
    },
    {
      title: 'Perguntas Frequentes',
      href: '/perguntas-respostas',
      icon: <HelpIcon />,
      external: false,
    },
  ];

  // Links para Servidores Públicos
  const serverLinks: ImportantLink[] = [
    parametersPortal?.urlPaycheck && {
      title: 'Contracheque Online',
      href: parametersPortal.urlPaycheck,
      icon: <ReceiptIcon />,
      external: true,
    },
    parametersPortal?.urlDaily && {
      title: 'Consultar Diárias',
      href: parametersPortal.urlDaily,
      icon: <FlightIcon />,
      external: true,
    },
    {
      title: 'Portal do Servidor',
      href: `http://airdoc.com.br/portal-servidor?codEntidade=${cnpj}&empresa=3851`,
      icon: <BadgeIcon />,
      external: true,
    },
    {
      title: 'Estrutura Administrativa',
      href: '/estrutura-administrativa',
      icon: <AccountTreeIcon />,
      external: false,
    },
    {
      title: 'Sistema e-GED',
      href: `http://airdoc.com.br/minuspaper?codEntidade=${cnpj}&empresa=3851`,
      icon: <FolderOpenIcon />,
      external: true,
    }
  ].filter(Boolean) as ImportantLink[];

  // Links para Empresas
  const companyLinks: ImportantLink[] = [
    {
      title: 'Processos Licitatórios',
      href: '/licitacoes',
      icon: <GavelIcon />,
      external: false,
    },
    cityNameNoSpaces && {
      title: 'Consulta de Processos (SEI)',
      href: `https://sip.${cityNameNoSpaces}.seibahia.ba.gov.br/login.php?sigla_orgao_sistema=${cityNameNoSpaces.toUpperCase()}&sigla_sistema=SEI`,
      icon: <DescriptionIcon />,
      external: true,
    },
    {
      title: 'Contratos e Aquisições',
      href: '/contratos-aquisicoes',
      icon: <BusinessIcon />,
      external: false,
    },
    parametersPortal?.urlNfse && {
      title: 'Nota Fiscal Eletrônica',
      href: parametersPortal.urlNfse,
      icon: <ReceiptIcon />,
      external: true,
    },
    {
      title: 'Convênios e Repasses',
      href: '/convenios-repasses',
      icon: <HandshakeIcon />,
      external: false,
    },
    {
      title: 'Certidões Online',
      href: `http://airdoc.com.br/certidoes?codEntidade=${cnpj}&empresa=3851`,
      icon: <VerifiedIcon />,
      external: true,
    },
  ].filter(Boolean) as ImportantLink[];

  return (
    <PageSection>
      <Container maxWidth={false}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <HomeSectionTitle>
              Serviços<hr />
            </HomeSectionTitle>
          </Grid>

          {/* CATEGORIA: CIDADÃO */}
          <Grid item xs={12}>
            <CategorySection>
              <CategoryTitle>👥 Cidadão</CategoryTitle>
              <Grid container spacing={3}>
                {citizenLinks.map((link, index) => (
                  <Grid key={index} item xs={6} sm={4} md={3} lg={2}>
                    <LinkButtonCard {...link} />
                  </Grid>
                ))}
              </Grid>
            </CategorySection>
          </Grid>

          {/* CATEGORIA: SERVIDOR PÚBLICO */}
          <Grid item xs={12}>
            <CategorySection>
              <CategoryTitle>👤 Servidor Público</CategoryTitle>
              <Grid container spacing={3}>
                {serverLinks.map((link, index) => (
                  <Grid key={index} item xs={6} sm={4} md={3} lg={2}>
                    <LinkButtonCard {...link} />
                  </Grid>
                ))}
              </Grid>
            </CategorySection>
          </Grid>

          {/* CATEGORIA: EMPRESAS */}
          <Grid item xs={12}>
            <CategorySection>
              <CategoryTitle>🏢 Empresas</CategoryTitle>
              <Grid container spacing={3}>
                {companyLinks.map((link, index) => (
                  <Grid key={index} item xs={6} sm={4} md={3} lg={2}>
                    <LinkButtonCard {...link} />
                  </Grid>
                ))}
              </Grid>
            </CategorySection>
          </Grid>
        </Grid>
      </Container>
    </PageSection>
  );
}
